import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { config } from '../lib/config'
import { demoStore } from '../features/demo/store'
import { supabase } from '../lib/supabaseClient'
import type { PostRow } from '../types/db'
import { Button } from '../components/Button'
import { EmptyState } from '../components/States'

export function ProfilePage() {
  const { profile, logout, isGuest } = useAuth()
  const navigate = useNavigate()
  const { data: dbPosts = [] } = useQuery({
    queryKey: ['posts', 'author', profile?.id],
    enabled: Boolean(profile?.id) && !config.e2e,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('author', profile!.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as PostRow[]
    },
  })
  const posts = config.e2e
    ? demoStore.posts().filter((post) => post.author === profile?.id)
    : dbPosts
  const xp = Math.min(100, ((profile?.level ?? 1) * 23) % 100)
  const leave = async () => {
    if (!window.confirm('Log out of ZENITH on this device?')) return
    await logout()
    navigate('/forums')
  }
  return (
    <div className="page profile-page">
      <section className="profile-hero">
        <div className="profile-avatar">{profile?.codename.slice(0, 2)}</div>
        <span className="eyebrow">Member profile</span>
        <h1>{profile?.codename}</h1>
        <p>{profile?.bio || 'Read-only Guild observer.'}</p>
      </section>
      <div className="profile-stats">
        <article>
          <span>Level</span>
          <strong>{profile?.level ?? 1}</strong>
        </article>
        <article>
          <span>First reports</span>
          <strong>🏆 {profile?.fr_count ?? 0}</strong>
        </article>
        <article>
          <span>Badge</span>
          <strong>{profile?.badge ?? 'Observer'}</strong>
        </article>
      </div>
      <section className="xp">
        <header>
          <span>Progress to next level</span>
          <b>{xp}%</b>
        </header>
        <div>
          <i style={{ width: `${xp}%` }} />
        </div>
      </section>
      <section>
        <h2 className="section-label">Your posts</h2>
        {posts.length === 0 ? (
          <EmptyState
            title="No reports yet"
            message="Your published observations will appear here."
          />
        ) : (
          posts.map((post) => (
            <article className="profile-post" key={post.id}>
              {post.title}
            </article>
          ))
        )}
      </section>
      <Button variant="secondary" onClick={() => void leave()} data-testid="logout">
        {isGuest ? 'Leave guest mode' : 'Log out'}
      </Button>
    </div>
  )
}
